// Per-project header metadata for the dashboard's TopBar.
//
// We could surface a dedicated `GET /api/projects/:id` server endpoint, but
// the same-session `/api/projects` list already carries everything the
// TopBar needs (org + name + last activity). This hook reuses that listing
// through a module-level promise cache so the dashboard doesn't re-fetch
// on every navigation between sessions.

import { useEffect, useState } from "react";

import { fetchProjects, type Project } from "../projects/useProjects";

import { UnauthorizedError } from "../lib/http";

export interface ProjectMeta {
  readonly id: number;
  readonly name: string;
  readonly org: string;
  /**
   * GitLab's `last_activity_at`, RAW, or undefined when the project list did
   * not answer.
   *
   * An ISO instant rather than a rendered "4m ago": this module had its own
   * English formatter, so a German session's header read "aktualisiert 4m ago".
   * Relative time is chrome and follows the UI locale, which only the component
   * that renders it knows (`useRelativeTime`).
   */
  readonly lastTouchedIso?: string;
}

export interface ProjectMetaState {
  readonly data: ProjectMeta | null;
  readonly loading: boolean;
  readonly error: string | null;
}

let cache: Promise<Project[]> | null = null;
const loadProjects = (): Promise<Project[]> => {
  if (!cache) cache = fetchProjects();
  return cache;
};

// The group's display name, as the project picker shows it
// (`name_with_namespace`, "Group / Sub / Project"), not its path: a renamed
// group keeps its old path.
const splitNamespace = (
  p: Project,
): { readonly org: string; readonly name: string } => {
  const ns = p.name_with_namespace;
  const sep = ns.lastIndexOf(" / ");
  if (sep < 0) return { org: "", name: p.name };
  return { org: ns.slice(0, sep), name: p.name };
};

// Format an ISO timestamp as "12m ago" / "3h ago" / "2d ago".
export const useProjectMeta = (projectId: number): ProjectMetaState => {
  const [data, setData] = useState<ProjectMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadProjects()
      .then((projects) => {
        if (cancelled) return;
        const hit = projects.find((p) => p.id === projectId);
        if (!hit) {
          setData({
            id: projectId,
            name: `project ${projectId}`,
            org: "",
          });
          setLoading(false);
          return;
        }
        const { org, name } = splitNamespace(hit);
        setData({
          id: hit.id,
          name,
          org,
          ...(hit.last_activity_at !== undefined
            ? { lastTouchedIso: hit.last_activity_at }
            : {}),
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof UnauthorizedError) return;
        // Don't blow up the dashboard if the picker list fails — the TopBar
        // falls back to the bare projectId.
        setData({
          id: projectId,
          name: `project ${projectId}`,
          org: "",
        });
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
        // Force a retry next mount.
        cache = null;
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return { data, loading, error };
};
